import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Dumbbell, Plus, Search, Trash2, Edit2, X, Download, Upload, Link, Video, Loader2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['technical', 'tactical', 'physical'];

// Skill Focus organized by category
const SKILL_CATEGORIES = {
  TECHNICAL: [
    { value: 'ball_control', label: 'Ball Control' },
    { value: 'passing', label: 'Passing' },
    { value: 'dribbling', label: 'Dribbling' },
    { value: 'first_touch', label: 'First Touch' },
    { value: 'finishing', label: 'Finishing' },
    { value: 'shooting', label: 'Shooting' },
  ],
  PHYSICAL: [
    { value: 'speed', label: 'Speed' },
    { value: 'acceleration', label: 'Acceleration' },
    { value: 'agility', label: 'Agility' },
    { value: 'coordination', label: 'Coordination' },
    { value: 'balance', label: 'Balance' },
    { value: 'explosiveness', label: 'Explosiveness' },
    { value: 'endurance', label: 'Endurance' },
  ],
  'TACTICAL / IQ': [
    { value: 'positioning', label: 'Positioning' },
    { value: 'decision_making', label: 'Decision Making' },
    { value: 'vision', label: 'Vision' },
    { value: 'game_awareness', label: 'Game Awareness' },
    { value: 'reaction_time', label: 'Reaction Time' },
  ],
};

const DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];
const POSITIONS = ['All', 'Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'Winger'];

const TeamDrills = () => {
  const { currentTeam, isManager } = useOutletContext();
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterSkill, setFilterSkill] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDrill, setEditingDrill] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [seedingDefaults, setSeedingDefaults] = useState(false);
  const [videoInputMode, setVideoInputMode] = useState('link');
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [viewingDrill, setViewingDrill] = useState(null);
  const videoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '', category: 'technical', skill_focus: 'ball_control',
    description: '', duration: '', difficulty: 'intermediate', equipment: '', 
    video_url: '', position_focus: 'All'
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('team_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  useEffect(() => {
    if (currentTeam) {
      fetchDrills();
    }
  }, [currentTeam]);

  const fetchDrills = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/drills`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setDrills(data);
      }
    } catch (error) {
      toast.error('Failed to load drills');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const url = editingDrill 
        ? `${API_URL}/api/clubs/team/${currentTeam.team_id}/drills/${editingDrill.id}`
        : `${API_URL}/api/clubs/team/${currentTeam.team_id}/drills`;
      
      const method = editingDrill ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, sport: 'soccer' })
      });

      if (response.ok) {
        toast.success(editingDrill ? 'Drill updated' : 'Drill added');
        closeForm();
        fetchDrills();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save drill');
      }
    } catch (error) {
      toast.error('Failed to save drill');
    }
    setSubmitting(false);
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error('Video file too large. Maximum size is 100MB');
        return;
      }
      const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Allowed: MP4, MOV, WebM');
        return;
      }
      setSelectedVideoFile(file);
      setFormData({...formData, video_url: ''});
    }
  };

  const handleSeedDefaults = async () => {
    setSeedingDefaults(true);
    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/drills/seed-defaults`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(`Added ${data.total_added} default drills`);
        fetchDrills();
      } else {
        toast.error('Failed to add default drills');
      }
    } catch (error) {
      toast.error('Failed to add default drills');
    }
    setSeedingDefaults(false);
  };

  const handleEdit = (drill) => {
    setEditingDrill(drill);
    setFormData({
      name: drill.name, category: drill.category, skill_focus: drill.skill_focus,
      description: drill.description, duration: drill.duration, difficulty: drill.difficulty,
      equipment: drill.equipment || '', video_url: drill.video_url || '', position_focus: drill.position_focus || 'All'
    });
    setShowForm(true);
  };

  const handleDelete = async (drillId) => {
    if (!window.confirm('Delete this drill?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/clubs/team/${currentTeam.team_id}/drills/${drillId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      if (response.ok) {
        toast.success('Drill deleted');
        fetchDrills();
      } else {
        toast.error('Failed to delete drill');
      }
    } catch (error) {
      toast.error('Failed to delete drill');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingDrill(null);
    setVideoInputMode('link');
    setSelectedVideoFile(null);
    setFormData({
      name: '', category: 'technical', skill_focus: 'ball_control',
      description: '', duration: '', difficulty: 'intermediate', equipment: '', 
      video_url: '', position_focus: 'All'
    });
  };

  const clearFilters = () => {
    setFilterCategory('');
    setFilterDifficulty('');
    setFilterSkill('');
    setSearch('');
  };

  const filteredDrills = drills.filter(d => {
    const matchesSearch = d.name.toLowerCase().includes(search.toLowerCase()) ||
                          d.description?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !filterCategory || d.category === filterCategory;
    const matchesDifficulty = !filterDifficulty || d.difficulty === filterDifficulty;
    const matchesSkill = !filterSkill || d.skill_focus === filterSkill;
    return matchesSearch && matchesCategory && matchesDifficulty && matchesSkill;
  });

  const hasActiveFilters = filterCategory || filterDifficulty || filterSkill || search;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Manager view - read only
  if (isManager) {
    return (
      <div className="space-y-6" data-testid="team-drills">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Drill Library</h1>
          <p className="text-muted-foreground">{drills.length} drills in team library</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-sm">
          <p className="text-sm text-blue-400">
            As a Team Manager, you can view drills but cannot create or edit them.
            Contact your Team Coach to make changes.
          </p>
        </div>
        {drills.length === 0 ? (
          <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
            <Dumbbell className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No drills in library yet</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {drills.map((drill) => (
              <div key={drill.id} className="bg-card border border-border/40 p-4 rounded-sm">
                <h3 className="font-medium">{drill.name}</h3>
                <div className="flex flex-wrap gap-1 mt-1 mb-2">
                  <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{drill.category}</span>
                  <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{drill.difficulty}</span>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{drill.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-drills">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Drill Library</h1>
          <p className="text-muted-foreground">{drills.length} drills for {currentTeam?.team_name}</p>
        </div>
        <div className="flex gap-2">
          {drills.length === 0 && (
            <Button 
              variant="outline" 
              onClick={handleSeedDefaults}
              disabled={seedingDefaults}
            >
              <Download className="mr-2 h-4 w-4" />
              {seedingDefaults ? 'Adding...' : 'Add Default Drills'}
            </Button>
          )}
          <Button className="bg-primary text-black hover:bg-primary/90" onClick={() => setShowForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Drill
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
          <Input
            placeholder="Search drills..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-transparent"
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="p-2 bg-transparent border border-border rounded-sm text-sm"
          >
            <option value="" className="bg-card">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat} className="bg-card capitalize">{cat}</option>
            ))}
          </select>
          
          <select
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
            className="p-2 bg-transparent border border-border rounded-sm text-sm"
          >
            <option value="" className="bg-card">All Difficulties</option>
            {DIFFICULTIES.map(diff => (
              <option key={diff} value={diff} className="bg-card capitalize">{diff}</option>
            ))}
          </select>
          
          <select
            value={filterSkill}
            onChange={(e) => setFilterSkill(e.target.value)}
            className="p-2 bg-transparent border border-border rounded-sm text-sm"
          >
            <option value="" className="bg-card">All Skills</option>
            {Object.entries(SKILL_CATEGORIES).map(([category, skills]) => (
              <optgroup key={category} label={category} className="bg-card">
                {skills.map(skill => (
                  <option key={skill.value} value={skill.value} className="bg-card">
                    {skill.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X size={14} className="mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase">{editingDrill ? 'Edit Drill' : 'New Drill'}</h2>
              <button onClick={closeForm} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input placeholder="Drill Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              
              <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full p-2 bg-transparent border border-border rounded-sm">
                {CATEGORIES.map(cat => (<option key={cat} value={cat} className="bg-card capitalize">{cat}</option>))}
              </select>

              <div className="grid grid-cols-2 gap-4">
                <select 
                  value={formData.skill_focus} 
                  onChange={(e) => setFormData({...formData, skill_focus: e.target.value})} 
                  className="p-2 bg-transparent border border-border rounded-sm"
                >
                  {Object.entries(SKILL_CATEGORIES).map(([category, skills]) => (
                    <optgroup key={category} label={category} className="bg-card">
                      {skills.map(skill => (
                        <option key={skill.value} value={skill.value} className="bg-card">
                          {skill.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <select value={formData.difficulty} onChange={(e) => setFormData({...formData, difficulty: e.target.value})} className="p-2 bg-transparent border border-border rounded-sm">
                  {DIFFICULTIES.map(diff => (<option key={diff} value={diff} className="bg-card capitalize">{diff}</option>))}
                </select>
              </div>

              <select value={formData.position_focus} onChange={(e) => setFormData({...formData, position_focus: e.target.value})} className="w-full p-2 bg-transparent border border-border rounded-sm">
                {POSITIONS.map(pos => (<option key={pos} value={pos} className="bg-card">{pos}</option>))}
              </select>

              <MarkdownEditor 
                value={formData.description} 
                onChange={(value) => setFormData({...formData, description: value})} 
                placeholder="Describe the drill methodology, setup, execution steps, coaching points..."
                minRows={8}
                data-testid="drill-description-editor"
              />
              
              <div className="grid grid-cols-2 gap-4">
                <Input placeholder="Duration (e.g., 15 min)" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} />
                <Input placeholder="Equipment needed" value={formData.equipment} onChange={(e) => setFormData({...formData, equipment: e.target.value})} />
              </div>
              
              {/* Video Input Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted-foreground">Video (optional)</label>
                  <div className="flex gap-1 ml-auto">
                    <button
                      type="button"
                      onClick={() => { setVideoInputMode('link'); setSelectedVideoFile(null); }}
                      className={`px-3 py-1 text-xs rounded-sm flex items-center gap-1 ${videoInputMode === 'link' ? 'bg-primary text-black' : 'bg-muted'}`}
                    >
                      <Link size={12} /> Link
                    </button>
                    <button
                      type="button"
                      onClick={() => { setVideoInputMode('upload'); setFormData({...formData, video_url: ''}); }}
                      className={`px-3 py-1 text-xs rounded-sm flex items-center gap-1 ${videoInputMode === 'upload' ? 'bg-primary text-black' : 'bg-muted'}`}
                    >
                      <Upload size={12} /> Upload
                    </button>
                  </div>
                </div>
                
                {videoInputMode === 'link' ? (
                  <Input 
                    placeholder="Video URL (YouTube, Vimeo, etc.)" 
                    value={formData.video_url} 
                    onChange={(e) => setFormData({...formData, video_url: e.target.value})} 
                  />
                ) : (
                  <div className="space-y-2">
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                      onChange={handleVideoFileChange}
                      className="hidden"
                    />
                    <div 
                      onClick={() => videoInputRef.current?.click()}
                      className="border border-dashed border-border rounded-sm p-4 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    >
                      {selectedVideoFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <Video size={16} className="text-primary" />
                          <span className="text-sm">{selectedVideoFile.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(selectedVideoFile.size / (1024*1024)).toFixed(1)}MB)
                          </span>
                        </div>
                      ) : (
                        <div className="text-muted-foreground">
                          <Upload size={20} className="mx-auto mb-1" />
                          <p className="text-sm">Click to select video</p>
                          <p className="text-xs">MP4, MOV, WebM up to 100MB</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={closeForm} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-primary text-black">
                  {submitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                  ) : editingDrill ? 'Update' : 'Add Drill'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Drills Grid */}
      {filteredDrills.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <Dumbbell className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">{hasActiveFilters ? 'No drills match your filters' : 'No drills in your library yet'}</p>
          {!hasActiveFilters && (
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={handleSeedDefaults} disabled={seedingDefaults}>
                <Download className="mr-2 h-4 w-4" />
                Add Default Drills
              </Button>
              <Button onClick={() => setShowForm(true)}>Create Custom Drill</Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDrills.map((drill) => (
            <div key={drill.id} className="bg-card border border-border/40 p-4 rounded-sm relative">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium">{drill.name}</h3>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{drill.category}</span>
                    <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{drill.difficulty}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setViewingDrill(drill)} className="p-1.5 hover:bg-muted rounded-sm" title="View details">
                    <Eye size={14} />
                  </button>
                  <button onClick={() => handleEdit(drill)} className="p-1.5 hover:bg-muted rounded-sm">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(drill.id)} className="p-1.5 hover:bg-muted rounded-sm text-red-400">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <MarkdownRenderer content={drill.description} truncate maxLines={2} className="text-sm mb-2" />
              <div className="text-xs text-muted-foreground space-y-1">
                <p><span className="text-foreground">Skill:</span> {drill.skill_focus?.replace('_', ' ')}</p>
                {drill.duration && <p><span className="text-foreground">Duration:</span> {drill.duration}</p>}
                {drill.equipment && <p><span className="text-foreground">Equipment:</span> {drill.equipment}</p>}
                {drill.position_focus && drill.position_focus !== 'All' && (
                  <p><span className="text-foreground">Position:</span> {drill.position_focus}</p>
                )}
              </div>
              {drill.video_url && (
                <div className="mt-2">
                  <a href={drill.video_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                    <Video size={12} /> Watch Video
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* View Drill Detail Modal */}
      {viewingDrill && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-heading text-2xl uppercase">{viewingDrill.name}</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="text-xs px-2 py-1 bg-muted rounded capitalize">{viewingDrill.category}</span>
                  <span className="text-xs px-2 py-1 bg-muted rounded capitalize">{viewingDrill.difficulty}</span>
                  <span className="text-xs px-2 py-1 bg-muted rounded capitalize">{viewingDrill.skill_focus?.replace('_', ' ')}</span>
                </div>
              </div>
              <button onClick={() => setViewingDrill(null)} className="text-muted-foreground hover:text-foreground">
                <X size={24} />
              </button>
            </div>

            {/* Drill Info */}
            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              {viewingDrill.duration && (
                <div>
                  <span className="text-muted-foreground">Duration:</span>{' '}
                  <span className="text-foreground">{viewingDrill.duration}</span>
                </div>
              )}
              {viewingDrill.equipment && (
                <div>
                  <span className="text-muted-foreground">Equipment:</span>{' '}
                  <span className="text-foreground">{viewingDrill.equipment}</span>
                </div>
              )}
              {viewingDrill.position_focus && viewingDrill.position_focus !== 'All' && (
                <div>
                  <span className="text-muted-foreground">Position Focus:</span>{' '}
                  <span className="text-foreground">{viewingDrill.position_focus}</span>
                </div>
              )}
            </div>

            {/* Description with Markdown Rendering */}
            <div className="mb-6">
              <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Description</h3>
              <div className="bg-muted/30 p-4 rounded-sm border border-border/40">
                <MarkdownRenderer content={viewingDrill.description} />
              </div>
            </div>

            {/* Video */}
            {viewingDrill.video_url && (
              <div className="mb-6">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Video</h3>
                <a 
                  href={viewingDrill.video_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  <Video size={16} /> Watch Video
                </a>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setViewingDrill(null)} className="flex-1">
                Close
              </Button>
              <Button 
                onClick={() => { handleEdit(viewingDrill); setViewingDrill(null); }} 
                className="flex-1 bg-primary text-black"
              >
                <Edit2 size={16} className="mr-2" /> Edit Drill
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDrills;
